/*
 * Copyright (c) 2024. Bolmate B.V.
 */
import './style/platformChurn.scss';

import {ChangeFunction, CloseButton, Loader, SelectItemShape, WithBackground} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import React, {MouseEventHandler} from 'react';
import {Churn, DataSplitMethod} from "../../../util/interfaces";
import {BolmateButton, BolmateSelect, DvrdDatePicker} from "../../controls";

interface Props {
    onClose: MouseEventHandler;
    onSubmit: MouseEventHandler;
    onChangeFromDate: (value: IDate | null) => void;
    onChangeToDate: (value: IDate | null) => void;
    onChangeSplitMethod: ChangeFunction<DataSplitMethod>;
    data: Array<Churn>;
    fromDate: IDate | null;
    toDate: IDate | null;
    splitMethod: DataSplitMethod;
    loading: boolean;
    active: boolean;
}

const splitMethodItems: Array<SelectItemShape> = [
    {value: DataSplitMethod.HOUR, label: 'Per uur'},
    {value: DataSplitMethod.DAY, label: 'Per dag'},
    {value: DataSplitMethod.WEEK, label: 'Per week'},
    {value: DataSplitMethod.MONTH, label: 'Per maand'},
    {value: DataSplitMethod.QUARTER, label: 'Per kwartaal'},
    {value: DataSplitMethod.YEAR, label: 'Per jaar'}
];

export default function PlatformChurn(props: Props) {
    const {
        data, loading, active, splitMethod, onChangeSplitMethod, onClose, onChangeToDate, onChangeFromDate, toDate,
        fromDate, onSubmit
    } = props;

    function onClearDate(field: 'from' | 'to') {
        return function () {
            if (field === 'from') onChangeFromDate(null);
            else onChangeToDate(null);
        }
    }

    function renderBody() {
        return (
            <div className='body-container'>
                {renderFilters()}
                {renderChurnContainer()}
            </div>
        )
    }

    function renderFilters() {
        return (
            <div className='filter-bar'>
                <BolmateSelect onChange={onChangeSplitMethod} value={splitMethod} items={splitMethodItems}
                               label='Weergave'/>
                <div className='date-field'>
                    <DvrdDatePicker onChange={onChangeFromDate} value={fromDate} label='Vanaf datum'
                                    placeholder='dd-mm-jjjj' dateFormat='DD-MM-YYYY' closeOnChange/>
                    <CloseButton onClick={onClearDate('from')} className='clear-btn'/>
                </div>
                <div className='date-field'>
                    <DvrdDatePicker onChange={onChangeToDate} value={toDate} label='T/m datum'
                                    placeholder='dd-mm-jjjj' dateFormat='DD-MM-YYYY' closeOnChange/>
                    <CloseButton onClick={onClearDate('to')} className='clear-btn'/>
                </div>
                <BolmateButton onClick={onSubmit} label='Berekenen' disabled={loading}/>
            </div>
        )
    }

    function renderChurnContainer() {
        return (
            <div className='churn-container'>
                <Loader active={loading}/>
                <div className='row default-table-row head'>
                    <label>PERIODE</label>
                    <label>GEREGISTREERD</label>
                    <label>PROEFPERIODE</label>
                    <label>GRATIS</label>
                    <label>ACTIEF</label>
                    <label>OPGEZEGD</label>
                    <label>VERLOPEN</label>
                    <label>INACTIEF</label>
                </div>
                {data.map(renderChurn)}
            </div>
        )
    }

    function renderChurn(churn: Churn, index: number) {
        return (
            <div key={index} className='row default-table-row'>
                <label>{churn.period}{splitMethod === DataSplitMethod.HOUR ? ':00' : ''}</label>
                <label>{churn.registered}</label>
                {renderChurnCount(churn.trialing, churn.percentage_trialing)}
                {renderChurnCount(churn.free, churn.percentage_free)}
                {renderChurnCount(churn.active, churn.percentage_active)}
                {renderChurnCount(churn.canceled, churn.percentage_canceled)}
                {renderChurnCount(churn.expired, churn.percentage_expired)}
                {renderChurnCount(churn.inactive, churn.percentage_inactive)}
            </div>
        )
    }

    function renderChurnCount(count: number, percentage: number) {
        return <label>{count} <span className='percentage'>({percentage.toString().replace('.', ',')}%)</span></label>
    }

    return (
        <WithBackground active={active} onClose={onClose}>
            <div className='platform-churn default-popup'>
                <div className='header-container'>
                    <label className='header-title'>Abonnementen verloop</label>
                    <CloseButton onClick={onClose}/>
                </div>
                {renderBody()}
                <div className='footer-container btn'>
                    <BolmateButton onClick={onClose} label='Sluiten' disabled={loading}/>
                </div>
            </div>
        </WithBackground>
    )
}